import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PMCard, PMDataTable, PMMenu, PMSegmented, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { Page } from "@/ui/Page";
import { Character } from "@/ui/Character";
import { dayKey, shortDate } from "@/ui/format";
import { LineChart, seriesColor, Sparkline, type LinePoint, type LineSeries } from "./charts";
import { useMetricTypes, useReadings, type Reading } from "./queries";
import "./dashboards.css";

const DAY = 86_400_000;
const RANGES = { month: { label: "Month", days: 30 }, quarter: { label: "Quarter", days: 90 }, season: { label: "Season", days: 180 } } as const;
type Range = keyof typeof RANGES;

// "Stand count (k plants/ac)" → { name: "Stand count", unit: "k plants/ac" }
const split = (n?: string | null) => { const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(n ?? ""); return m ? { name: m[1], unit: m[2] } : { name: n ?? "Metric", unit: "" }; };
const num = (r: Reading) => { const v = parseFloat(r.value ?? ""); return Number.isFinite(v) ? v : null; };
const round = (v: number) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);

function dailyMean(rows: Reading[]): LinePoint[] {
  const by = new Map<string, number[]>();
  for (const r of rows) { const v = num(r); if (v === null) continue; const k = dayKey(r.created_at); by.set(k, [...(by.get(k) || []), v]); }
  return [...by.entries()].map(([k, vs]) => ({ t: new Date(k + "T12:00:00").getTime(), v: vs.reduce((a, b) => a + b, 0) / vs.length })).sort((a, b) => a.t - b.t);
}
function trend(points: LinePoint[]) {
  if (points.length < 3) return null;
  const k = Math.max(1, Math.floor(points.length / 3));
  const a = points.slice(0, k).reduce((s, p) => s + p.v, 0) / k, b = points.slice(-k).reduce((s, p) => s + p.v, 0) / k;
  return a ? Math.round(((b - a) / Math.abs(a)) * 100) : null;
}

// Metrics (core-features §4.4): trends in field inputs over time, no data work.
export function MetricsPage() {
  const search = useSearch({ from: "/t/$teamId/dashboards/metrics" });
  const nav = useNavigate();
  const { teamId, hierarchy } = useApp();
  const top = hierarchy.levels[0];
  const range: Range = (search.range as Range) in RANGES ? (search.range as Range) : "quarter";
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);
  const since = useMemo(() => new Date(Date.now() - RANGES[range].days * DAY).toISOString(), [range]);
  const types = useMetricTypes();
  const readings = useReadings(since);

  // Most informative first: metrics that change over time *within* a location
  // (mean per-location coefficient of variation). Fixed facts such as acres score ~0.
  const cv = (id: string) => {
    const by = new Map<string, number[]>();
    for (const r of readings.data) { if (r.metric_type_id !== id) continue; const v = num(r); if (v === null) continue; const k = r.map_feature_id ?? ""; by.set(k, [...(by.get(k) || []), v]); }
    const scores = [...by.values()].filter((vs) => vs.length > 1).map((vs) => { const m = vs.reduce((a, b) => a + b, 0) / vs.length; const sd = Math.sqrt(vs.reduce((a, b) => a + (b - m) ** 2, 0) / vs.length); return m ? sd / Math.abs(m) : 0; });
    return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  };
  const numericTypes = types.data.filter((t) => t.n > 0).map((t) => ({ t, v: cv(t.id) })).sort((a, b) => (b.v > 0.02 ? 1 : 0) - (a.v > 0.02 ? 1 : 0) || b.t.n - a.t.n || (a.t.name ?? "").localeCompare(b.t.name ?? "")).map((x) => x.t);
  const metricId = search.metric && types.data.some((t) => t.id === search.metric) ? search.metric : numericTypes[0]?.id;
  const metric = types.data.find((t) => t.id === metricId);
  const { name, unit } = split(metric?.name);
  const loc = search.location;

  // Stable entity colors: top-level locations ranked by all-time reading count in range.
  const tops = useMemo(() => {
    const n = new Map<string, { label: string; c: number }>();
    for (const r of readings.data) if (r.top_id) n.set(r.top_id, { label: r.top_label ?? "", c: (n.get(r.top_id)?.c || 0) + 1 });
    return [...n.entries()].sort((a, b) => b[1].c - a[1].c || a[1].label.localeCompare(b[1].label)).map(([id, v]) => ({ id, label: v.label }));
  }, [readings.data]);

  const rows = readings.data.filter((r) => r.metric_type_id === metricId && (!loc || r.top_id === loc) && num(r) !== null);
  const values = rows.map((r) => num(r)!);
  const avgPts = dailyMean(rows);
  const tr = trend(avgPts);
  const latest = rows.at(-1);

  const series: LineSeries[] = loc
    ? (() => {
        const fields = [...new Set(rows.map((r) => r.location_label ?? ""))].sort();
        return fields.slice(0, 4).map((f, i) => ({ key: f, label: f, color: seriesColor(i), points: rows.filter((r) => (r.location_label ?? "") === f).map((r) => ({ t: new Date(r.created_at!).getTime(), v: num(r)!, ref: r.field_note_id ?? undefined })) }));
      })()
    : [
        { key: "__avg", label: "Team average", color: "var(--pm-fg-1)", emphasis: true, points: avgPts },
        ...tops.slice(0, 4).map((tp, i) => ({ key: tp.id, label: tp.label, color: seriesColor(i), points: rows.filter((r) => r.top_id === tp.id).map((r) => ({ t: new Date(r.created_at!).getTime(), v: num(r)!, ref: r.field_note_id ?? undefined })) })).filter((s) => s.points.length),
      ];

  const openVisit = (id?: string) => id && nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: id } });
  const locName = loc ? tops.find((t) => t.id === loc)?.label : null;
  const fmtV = (v?: number | null) => (v == null ? "—" : round(v).toLocaleString());

  return (
    <Page chip="DASHBOARDS" title="Metrics" subtitle={`${name}${unit ? ` (${unit})` : ""} · ${locName ?? `All ${top.plural.toLowerCase()}`} · last ${RANGES[range].days} days`} wide
      actions={
        <div className="range-row">
          <PMMenu label={locName ?? `All ${top.plural.toLowerCase()}`} icon="map-pin" searchable align="right" value={loc ?? ""}
            options={[{ value: "", label: `All ${top.plural.toLowerCase()}` }, ...tops.map((t) => ({ value: t.id, label: t.label }))]}
            onChange={(v: string | string[]) => set({ location: (v as string) || undefined })} />
          <PMSegmented value={range} onChange={(v: string) => set({ range: v === "quarter" ? undefined : v })} options={Object.entries(RANGES).map(([value, r]) => ({ value, label: r.label }))} />
        </div>
      }>
      {readings.isLoading && !readings.data.length ? <PMSkeleton shape="rect" height={380} radius={16} /> : !numericTypes.length ? (
        <Character name="farmer" size={110} title="No measurements yet" line="Numbers captured in visit forms (stand counts, Brix, acres) chart here automatically." />
      ) : (
        <>
          <div className="met-multiples" role="list" aria-label="Metrics">
            {numericTypes.map((t) => {
              const tr2 = readings.data.filter((r) => r.metric_type_id === t.id && (!loc || r.top_id === loc));
              const pts = dailyMean(tr2);
              const d = trend(pts);
              const s = split(t.name);
              return (
                <button key={t.id} role="listitem" className={`met-mini ${t.id === metricId ? "is-on" : ""}`} onClick={() => set({ metric: t.id })} aria-pressed={t.id === metricId}>
                  <span className="met-mini-name">{s.name}</span>
                  <span className="met-mini-val">{fmtV(pts.at(-1)?.v)}<small>{s.unit}</small>{d != null && <small className={d > 0 ? "trend-up" : d < 0 ? "trend-down" : "trend-flat"}>{d > 0 ? "▲" : d < 0 ? "▼" : "•"} {Math.abs(d)}%</small>}</span>
                  <Sparkline points={pts} color={t.id === metricId ? "var(--dv-1)" : "var(--dv-muted)"} />
                </button>
              );
            })}
          </div>

          <PMCard eyebrow={name} icon="bar-chart-outline" count={rows.length}>
            {rows.length ? (
              <>
                <div className="met-stats">
                  <div className="met-stat"><span>Latest</span><strong>{fmtV(latest ? num(latest) : null)}</strong><small>{latest ? shortDate(latest.created_at) : ""}</small></div>
                  <div className="met-stat"><span>Average</span><strong>{fmtV(values.reduce((a, b) => a + b, 0) / values.length)}</strong><small>{unit}</small></div>
                  <div className="met-stat"><span>Range</span><strong>{fmtV(Math.min(...values))}–{fmtV(Math.max(...values))}</strong><small>{unit}</small></div>
                  <div className="met-stat"><span>Trend</span><strong className={tr == null ? "" : tr > 0 ? "trend-up" : tr < 0 ? "trend-down" : "trend-flat"}>{tr == null ? "—" : `${tr > 0 ? "▲" : tr < 0 ? "▼" : ""} ${Math.abs(tr)}%`}</strong><small>first vs last third</small></div>
                </div>
                <LineChart series={series} unit={unit} height={320} onPoint={(p) => openVisit(p.ref)}
                  summary={`${name} over the last ${RANGES[range].days} days: ${rows.length} readings, average ${fmtV(values.reduce((a, b) => a + b, 0) / values.length)} ${unit}, trend ${tr ?? 0}%.`} />
              </>
            ) : <p className="hint">No {name.toLowerCase()} readings in this range{locName ? ` for ${locName}` : ""}.</p>}
          </PMCard>

          <PMCard eyebrow="Recent readings" icon="document-outline" count={Math.min(rows.length, 25)}>
            <PMDataTable rowKey="id" rows={[...rows].reverse().slice(0, 25)} onRowClick={(r: any) => openVisit(r.field_note_id)} emptyText="No readings"
              columns={[
                { key: "created_at", label: "Date", sortable: true, render: (r: any) => shortDate(r.created_at), sortValue: (r: any) => r.created_at },
                { key: "location_label", label: hierarchy.levels.at(-1)!.label, sortable: true },
                { key: "top_label", label: top.label, sortable: true },
                { key: "value", label: `${name}${unit ? ` (${unit})` : ""}`, align: "right", sortable: true, render: (r: any) => <strong>{fmtV(num(r))}</strong>, sortValue: (r: any) => num(r) ?? 0 },
              ]} />
          </PMCard>
        </>
      )}
    </Page>
  );
}
